/**
 * Warranty draft / issued warranty domain — shared types and pure helpers.
 *
 * Lifecycle notes (see docs/architecture/lifecycle-convergence.md):
 * - Authoritative records: warranty_cards + warranty_items
 * - Serial entry syncs products immediately (searchable before issuance)
 * - Only issued items appear in CLARITY1
 * - Payment reversal: draft → on_hold; issued → review_required (never silent delete)
 * - Original warranty holder is immutable after issuance
 */

import { canPresentWarrantyAsVerified } from "./warrantyPeriodAuthority";
import { normalizeUserRole } from "./userRoles";

export const WARRANTY_CARD_STATUSES = [
  "draft",
  "pending_approval",
  "changes_requested",
  "issued",
  "voided",
  "on_hold",
  "review_required",
] as const;
export type WarrantyCardStatus = (typeof WARRANTY_CARD_STATUSES)[number];

export function warrantyCardStaffStatusLabel(status: string | null | undefined): string {
  switch (String(status ?? "").trim().toLowerCase()) {
    case "issued":
      return "WARRANTY LIVE";
    case "pending_approval":
      return "REVIEW WARRANTY";
    case "draft":
      return "WARRANTY DRAFT";
    case "changes_requested":
      return "CHANGES REQUESTED";
    case "on_hold":
      return "WARRANTY ON HOLD";
    case "review_required":
      return "REVIEW REQUIRED";
    case "voided":
      return "WARRANTY VOIDED";
    default:
      return "VIEW WARRANTY ITEMS";
  }
}

export const WARRANTY_EDITABLE_STATUSES: readonly WarrantyCardStatus[] = [
  "draft",
  "changes_requested",
  "on_hold",
] as const;

export const WARRANTY_SUBMITTABLE_STATUSES: readonly WarrantyCardStatus[] = [
  "draft",
  "changes_requested",
  "on_hold",
] as const;

export function isWarrantyEditableStatus(status: string | null | undefined): boolean {
  return WARRANTY_EDITABLE_STATUSES.includes(status as WarrantyCardStatus);
}

export function isWarrantySubmittableStatus(status: string | null | undefined): boolean {
  return WARRANTY_SUBMITTABLE_STATUSES.includes(status as WarrantyCardStatus);
}

export const WARRANTY_SOURCE_INVOICE_TYPES = [
  "deal",
  "portal",
  "retail_legacy",
  "manual",
] as const;
export type WarrantySourceInvoiceType = (typeof WARRANTY_SOURCE_INVOICE_TYPES)[number];

export const WARRANTY_IMAGE_SOURCE_TYPES = [
  "proposal",
  "project_catalogue",
  "sales_catalogue",
  "existing_product",
  "image_search",
  "staff_upload",
  "none",
] as const;
export type WarrantyImageSourceType = (typeof WARRANTY_IMAGE_SOURCE_TYPES)[number];

export const WARRANTY_AUDIT_ACTIONS = [
  "draft_created",
  "source_invoice_selected",
  "product_added",
  "product_removed",
  "product_excluded",
  "product_included",
  "serial_entered",
  "serial_changed",
  "existing_product_reused",
  "duration_changed",
  "start_date_changed",
  "manufacturer_terms_changed",
  "image_selected",
  "image_uploaded",
  "image_removed",
  "draft_saved",
  "submitted_for_approval",
  "changes_requested",
  "resubmitted_for_approval",
  "approval_notification_sent",
  "approval_notification_failed",
  "approval_notification_retried",
  "warranty_approved_and_issued",
  "warranty_issued",
  "warranty_voided",
  "payment_reversal_hold",
  "payment_reversal_review",
  "ownership_correction",
  "eligibility_override",
  "empty_draft_recovered",
  "admin_pending_edit",
  "warranty_period_verified",
  "warranty_period_reverified",
] as const;
export type WarrantyAuditAction = (typeof WARRANTY_AUDIT_ACTIONS)[number];

export const WARRANTY_ELIGIBILITY_BLOCK_REASONS = {
  unpaid: "Warranty draft available after final payment is received.",
  partial: "Warranty draft available after final payment is received.",
  deposit_only: "Warranty draft available after final payment is received.",
  scope_incomplete: "Warranty draft available after final payment is received.",
  voided: "This invoice is voided and cannot create a warranty draft.",
  duplicate: "A warranty draft already exists for this commercial scope.",
  issued: "Warranty items have already been issued for this commercial scope.",
  on_hold: "Warranty draft is on hold after payment reversal.",
  review_required: "Issued warranty requires staff review after payment reversal.",
} as const;

export type WarrantyEligibilityBlockReason = keyof typeof WARRANTY_ELIGIBILITY_BLOCK_REASONS;

/** Roles that may create/edit warranty drafts. */
export const WARRANTY_DRAFT_EDIT_ROLES = new Set(["sales", "admin"]);
/** Roles that may issue or void warranties. */
export const WARRANTY_ISSUE_ROLES = new Set(["admin"]);
/**
 * Roles that may view warranty state on the customer card.
 * Matches staff who can open a customer card, including programming / installer / inventory.
 */
export const WARRANTY_VIEW_ROLES = new Set([
  "sales",
  "admin",
  "finance",
  "service",
  "programming",
  "installer",
  "inventory",
]);
/** Roles that may perform audited ownership / eligibility overrides. */
export const WARRANTY_OVERRIDE_ROLES = new Set(["admin"]);

export function canEditWarrantyDraft(role: string | null | undefined): boolean {
  return WARRANTY_DRAFT_EDIT_ROLES.has(normalizeUserRole(role));
}

export function canIssueWarranty(role: string | null | undefined): boolean {
  return WARRANTY_ISSUE_ROLES.has(normalizeUserRole(role));
}

export function canViewWarranty(role: string | null | undefined): boolean {
  return WARRANTY_VIEW_ROLES.has(normalizeUserRole(role));
}

export function canOverrideWarranty(role: string | null | undefined): boolean {
  return WARRANTY_OVERRIDE_ROLES.has(normalizeUserRole(role));
}

/** Admin may edit a pending_approval warranty before approve/issue (Sales may not). */
export function canAdminEditPendingWarranty(
  role: string | null | undefined,
  status: string | null | undefined,
): boolean {
  return canIssueWarranty(role) && status === "pending_approval";
}

const ADMIN_DIRECT_ISSUE_STATUSES: readonly WarrantyCardStatus[] = [
  "draft",
  "changes_requested",
  "pending_approval",
] as const;

/**
 * Admin may issue from draft/changes_requested in one step (no self-submit loop)
 * or approve a pending_approval card. Non-admin cannot issue.
 */
export function canAdminDirectIssueWarranty(
  role: string | null | undefined,
  status: string | null | undefined,
): boolean {
  return (
    canIssueWarranty(role) &&
    ADMIN_DIRECT_ISSUE_STATUSES.includes(status as WarrantyCardStatus)
  );
}

/** Preserve display serial; normalise for uniqueness / duplicate detection. */
export function normalizeWarrantySerial(raw: string): string {
  return raw.trim().toUpperCase().replace(/[\s\-_.]/g, "");
}

export function displayWarrantySerial(raw: string): string {
  return raw.trim();
}

export function isSerialNotApplicable(flag: boolean | number | null | undefined): boolean {
  return flag === true || flag === 1;
}

/** Add calendar months, clamping end-of-month (e.g. 31 Jan + 1 month → 28/29 Feb). */
export function addWarrantyMonths(start: Date, months: number): Date {
  const y = start.getUTCFullYear();
  const m = start.getUTCMonth();
  const d = start.getUTCDate();
  const target = new Date(Date.UTC(y, m + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target;
}

/**
 * Manufacturer warranty expiry is typically start + duration months − 1 day
 * (e.g. 12 May 2024 + 24 months → 11 May 2026).
 */
export function calculateWarrantyExpiry(start: Date, durationMonths: number): Date {
  if (!Number.isFinite(durationMonths) || durationMonths <= 0) {
    throw new Error("Warranty duration must be a positive number of months");
  }
  const end = addWarrantyMonths(start, durationMonths);
  end.setUTCDate(end.getUTCDate() - 1);
  return end;
}

export function formatWarrantyDurationLabel(months: number): string {
  if (months === 12) return "1 year coverage";
  if (months % 12 === 0) return `${months / 12} years coverage`;
  if (months === 1) return "1 month coverage";
  return `${months} months coverage`;
}

export type WarrantyItemCoverageStatus = "active" | "expiring_soon" | "expired" | "unknown";

const EXPIRING_SOON_DAYS = 90;

export function classifyWarrantyCoverageStatus(
  expiry: Date | string | null | undefined,
  now = new Date(),
): WarrantyItemCoverageStatus {
  if (!expiry) return "unknown";
  const end = expiry instanceof Date ? expiry : new Date(expiry);
  if (!Number.isFinite(end.getTime())) return "unknown";
  const ms = end.getTime() - now.getTime();
  if (ms < 0) return "expired";
  if (ms <= EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000) return "expiring_soon";
  return "active";
}

export function buildCommercialScopeKey(input: {
  sourceInvoiceType: WarrantySourceInvoiceType;
  sourceInvoiceId: number;
  proposalId?: number | null;
  dealId?: number | null;
}): string {
  if (input.proposalId) return `proposal:${input.proposalId}`;
  if (input.dealId) return `deal:${input.dealId}`;
  return `${input.sourceInvoiceType}:${input.sourceInvoiceId}`;
}

export function defaultWarrantyImageAlt(brand: string, model: string, finish?: string | null): string {
  const joined = [brand, model, finish].map((p) => (p ?? "").trim()).filter(Boolean).join(" ");
  // Keep alt within warranty_items.imageAlt varchar(512).
  if (joined.length <= 512) return joined;
  const slice = joined.slice(0, 512);
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace >= 40 ? slice.slice(0, lastSpace) : slice).trim();
}

export function defaultImageSearchQuery(brand: string, model: string, finish?: string | null): string {
  return defaultWarrantyImageAlt(brand, model, finish);
}

export const WARRANTY_NON_TRANSFERABILITY_STATEMENT =
  "This warranty is issued to the original purchaser named on the Clarity sales record and is non-transferable. Sale, gift or other transfer of the product does not transfer this warranty coverage.";

export const WARRANTY_SALES_TERMS_STATEMENT =
  "Clarity's Terms & Conditions of Sale shall apply unless otherwise agreed in writing.";
export const WARRANTY_SALES_TERMS_URL = "https://portal.claritygroup.com.sg/terms/retail";

export const WARRANTY_MANUFACTURER_AUTHORITY_STATEMENT =
  "Coverage remains subject to the applicable manufacturer’s original warranty terms and warranty period.";

export const WARRANTY_CLARITY_SUPPORT_EXPLANATION =
  "This product is covered according to the manufacturer’s applicable warranty terms and stated warranty period. Clarity can assist with assessment and submission of eligible claims.";

export const WARRANTY_MANDATORY_RIGHTS_NOTE =
  "Where mandatory law or the manufacturer’s applicable terms provide rights that cannot be restricted by the non-transferability rule, those rights remain unaffected.";

export type WarrantyInvoiceActionState =
  | { kind: "create"; eligible: true }
  | { kind: "continue"; eligible: true; warrantyCardId: number }
  | { kind: "view"; eligible: true; warrantyCardId: number }
  | {
      kind: "disabled";
      eligible: false;
      reason: WarrantyEligibilityBlockReason;
      message: string;
      warrantyCardId?: number;
    };

export function resolveWarrantyInvoiceAction(input: {
  eligible: boolean;
  blockReason?: WarrantyEligibilityBlockReason | null;
  existingStatus?: WarrantyCardStatus | null;
  existingCardId?: number | null;
}): WarrantyInvoiceActionState {
  const cardId = input.existingCardId ?? undefined;
  const status = input.existingStatus ?? null;

  if (status === "issued" || status === "review_required") {
    return { kind: "view", eligible: true, warrantyCardId: cardId! };
  }
  if (status === "pending_approval") {
    return { kind: "view", eligible: true, warrantyCardId: cardId! };
  }
  if (status === "draft" || status === "on_hold" || status === "changes_requested") {
    if (!input.eligible && status === "on_hold") {
      return {
        kind: "disabled",
        eligible: false,
        reason: "on_hold",
        message: WARRANTY_ELIGIBILITY_BLOCK_REASONS.on_hold,
        warrantyCardId: cardId,
      };
    }
    return { kind: "continue", eligible: true, warrantyCardId: cardId! };
  }
  if (!input.eligible) {
    const reason = input.blockReason ?? "unpaid";
    return {
      kind: "disabled",
      eligible: false,
      reason,
      message: WARRANTY_ELIGIBILITY_BLOCK_REASONS[reason],
      warrantyCardId: cardId,
    };
  }
  return { kind: "create", eligible: true };
}

export type WarrantyIssuanceValidationIssue = {
  code:
    | "not_eligible"
    | "no_included_items"
    | "missing_brand_model"
    | "missing_serial"
    | "invalid_serial"
    | "serial_conflict"
    | "missing_start_date"
    | "invalid_duration"
    | "expiry_mismatch"
    | "missing_manufacturer_label"
    | "unverified_warranty_period"
    | "ownership_mismatch"
    | "image_not_persisted"
    | "card_not_draft";
  message: string;
  itemId?: number;
};

export function validateWarrantyItemForIssuance(item: {
  id?: number;
  included: boolean;
  brand: string;
  model: string;
  serialNumber: string | null;
  serialNotApplicable: boolean;
  warrantyStartDate: Date | string | null;
  warrantyDurationMonths: number | null;
  warrantyExpiryDate: Date | string | null;
  manufacturerWarrantyLabel: string | null;
  warrantyPeriodSource?: string | null;
  /** Required for included items — Clarity-managed R2 key. */
  imageStorageKey?: string | null;
  /** Required for included items — same-origin `/api/warranty-images/...` URL. */
  imageUrl?: string | null;
}): WarrantyIssuanceValidationIssue[] {
  if (!item.included) return [];
  const issues: WarrantyIssuanceValidationIssue[] = [];
  const id = item.id;

  if (!item.brand?.trim() || !item.model?.trim()) {
    issues.push({
      code: "missing_brand_model",
      message: "Brand and model are required for included items.",
      itemId: id,
    });
  }

  if (!item.serialNotApplicable) {
    const serial = (item.serialNumber ?? "").trim();
    if (!serial) {
      issues.push({
        code: "missing_serial",
        message: "Serial number is required unless marked not applicable.",
        itemId: id,
      });
    } else if (normalizeWarrantySerial(serial).length < 2) {
      issues.push({
        code: "invalid_serial",
        message: "Serial number is too short after normalisation.",
        itemId: id,
      });
    }
  }

  if (!item.warrantyStartDate) {
    issues.push({
      code: "missing_start_date",
      message: "Warranty start date is required.",
      itemId: id,
    });
  }

  const months = item.warrantyDurationMonths;
  if (months == null || !Number.isFinite(months) || months <= 0) {
    issues.push({
      code: "invalid_duration",
      message: "Warranty duration (months) must be a positive number.",
      itemId: id,
    });
  } else if (item.warrantyStartDate) {
    const start =
      item.warrantyStartDate instanceof Date
        ? item.warrantyStartDate
        : new Date(item.warrantyStartDate);
    if (Number.isFinite(start.getTime())) {
      const expected = calculateWarrantyExpiry(start, months);
      const actual = item.warrantyExpiryDate
        ? item.warrantyExpiryDate instanceof Date
          ? item.warrantyExpiryDate
          : new Date(item.warrantyExpiryDate)
        : null;
      if (
        !actual ||
        !Number.isFinite(actual.getTime()) ||
        actual.toISOString().slice(0, 10) !== expected.toISOString().slice(0, 10)
      ) {
        issues.push({
          code: "expiry_mismatch",
          message: "Warranty expiry does not match start date + duration.",
          itemId: id,
        });
      }
    }
  }

  if (!(item.manufacturerWarrantyLabel ?? "").trim()) {
    issues.push({
      code: "missing_manufacturer_label",
      message: "Manufacturer warranty label or reference is required.",
      itemId: id,
    });
  }

  if (!canPresentWarrantyAsVerified(item.warrantyPeriodSource, item.warrantyDurationMonths)) {
    issues.push({
      code: "unverified_warranty_period",
      message:
        "Manufacturer warranty is not yet verified. Verify from an official manufacturer source or enter it before issuing to the customer.",
      itemId: id,
    });
  }

  if (!hasDurableWarrantyImage(item)) {
    const label = [item.brand, item.model].map((p) => (p ?? "").trim()).filter(Boolean).join(" ");
    issues.push({
      code: "image_not_persisted",
      message: label
        ? `${label}: add a Clarity-stored product image (FIND IMAGE or UPLOAD IMAGE) before submission.`
        : "Each included product needs a Clarity-stored product image before submission.",
      itemId: id,
    });
  }

  return issues;
}

/** Same-origin warranty image path persisted in DB (not a signed/private R2 URL). */
export function isWarrantyManagedImageUrl(url: string | null | undefined): boolean {
  const value = (url ?? "").trim();
  if (!value) return false;
  if (value.startsWith("/api/warranty-images/")) return true;
  return /^https?:\/\/[^/]+\/api\/warranty-images\//i.test(value);
}

/**
 * Included warranty items must have a durable Clarity-managed image before
 * submission/issuance — for every image source type (proposal, catalogue,
 * search, upload, etc.). External URLs alone are not sufficient.
 */
export function hasDurableWarrantyImage(item: {
  imageStorageKey?: string | null;
  imageUrl?: string | null;
}): boolean {
  return Boolean((item.imageStorageKey ?? "").trim()) && isWarrantyManagedImageUrl(item.imageUrl);
}

/** Staff-visible warning when an inherited proposal/catalogue image could not be copied. */
export const WARRANTY_IMAGE_IMPORT_FAILED_NOTE =
  "Product image could not be copied into Clarity storage. Use FIND IMAGE or UPLOAD IMAGE.";
